import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  countWorkingDays,
  parseDate,
  utcCalendarTodayNoon,
} from '../../../../../utils/countWorkingDays';

const GREEN = '#1D9E75';
const RED = '#E24B4A';
const NEUTRAL = 'action.hover';

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

const scheduleGroupLabelSx = {
  fontSize: '9px',
  fontWeight: 500,
  color: 'text.secondary',
  opacity: 0.82,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  lineHeight: 1.2,
  mb: 0.5,
  pl: 0.5,
};

function fmtEndpoint(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const mo = d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
  const day = d.getUTCDate();
  const year = d.getUTCFullYear();
  const currentYear = new Date().getFullYear();
  return year === currentYear
    ? `${mo} ${day}`
    : `${mo} ${day} '${String(year).slice(2)}`;
}

function fmtTodayPill() {
  const d = new Date();
  const mo = d.toLocaleDateString('en-US', { month: 'short' });
  const day = d.getDate();
  const year = d.getFullYear();
  const currentYear = new Date().getFullYear();
  return year === currentYear
    ? `${mo} ${day}`
    : `${mo} ${day} '${String(year).slice(2)}`;
}

/** UTC calendar day for date-only ISO strings. */
function utcDayMs(isoStr) {
  if (!isoStr) return null;
  const d = new Date(isoStr);
  if (!Number.isFinite(d.getTime())) return null;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function computeTimeline(startDate, endDate, actualStartDate) {
  if (!startDate || !endDate) {
    return { kind: 'nodates' };
  }
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  const today = utcCalendarTodayNoon();
  if (
    Number.isNaN(start.getTime()) ||
    Number.isNaN(end.getTime()) ||
    start.getTime() > end.getTime()
  ) {
    return { kind: 'invalidSpan' };
  }

  const plannedWorking = countWorkingDays(start, end);
  if (plannedWorking <= 0) {
    return { kind: 'invalidSpan' };
  }

  if (today.getTime() < start.getTime()) {
    const next = new Date(today.getTime());
    next.setUTCDate(next.getUTCDate() + 1);
    const days = Math.max(1, countWorkingDays(next, start));
    const planStartDay = utcDayMs(startDate);
    const actStartDay = utcDayMs(actualStartDate);
    const startedAhead =
      actStartDay != null && planStartDay != null && actStartDay < planStartDay;

    return {
      kind: 'pre',
      days,
      startedAhead,
      startLabel: fmtEndpoint(startDate),
      endLabel: fmtEndpoint(endDate),
    };
  }

  const elapsedWorking = countWorkingDays(start, today);
  const pct = (elapsedWorking / plannedWorking) * 100;

  if (today.getTime() > end.getTime()) {
    return {
      kind: 'overrun',
      overrunPct: Math.round(pct),
      startLabel: fmtEndpoint(startDate),
      endLabel: fmtEndpoint(endDate),
    };
  }

  const tickFrac = (elapsedWorking / plannedWorking) * 100;
  let pillFrac = tickFrac;
  if (pillFrac < 12) pillFrac = 12;
  else if (pillFrac > 88) pillFrac = 88;

  return {
    kind: 'active',
    schedPct: Math.round(pct),
    tickFrac,
    pillFrac,
    startLabel: fmtEndpoint(startDate),
    endLabel: fmtEndpoint(endDate),
  };
}

// Space reserved above the bar for the "today" pill on active lanes. When
// `reservePillSpace` is set (two-lane prefab view), inactive lanes reserve the
// same space so the Schedule / Hours / Earned rows line up across both cards.
const PILL_RESERVE_MT = 3.5;
const BAR_BASE_MT = 0.5;

function renderPlanBar(timeline, reservePillSpace = false) {
  const reservedMt = reservePillSpace ? PILL_RESERVE_MT : BAR_BASE_MT;

  if (
    timeline.kind === 'nodates' ||
    timeline.kind === 'invalidSpan' ||
    timeline.kind === 'pre'
  ) {
    return (
      <Box
        sx={{
          position: 'relative',
          height: 8,
          borderRadius: 1,
          bgcolor: NEUTRAL,
          overflow: 'hidden',
          mt: reservedMt,
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        height: 8,
        borderRadius: 1,
        bgcolor: NEUTRAL,
        overflow: timeline.kind === 'active' ? 'visible' : 'hidden',
        mt: timeline.kind === 'active' ? PILL_RESERVE_MT : reservedMt,
      }}
    >
      {timeline.kind === 'active' && (
        <>
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              height: '100%',
              width: `${clamp(timeline.tickFrac, 0, 100)}%`,
              bgcolor: GREEN,
              borderRadius: 1,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              left: `${clamp(timeline.tickFrac, 0, 100)}%`,
              bottom: 0,
              transform: 'translateX(-50%)',
              width: 2,
              height: '100%',
              bgcolor: 'text.primary',
              borderRadius: 0.5,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              left: `${clamp(timeline.pillFrac, 0, 100)}%`,
              bottom: '100%',
              transform: 'translateX(-50%)',
              mb: 0.25,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: '10px',
                px: 0.75,
                py: 0.25,
                borderRadius: 10,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                whiteSpace: 'nowrap',
              }}
            >
              {fmtTodayPill()}
            </Typography>
            <Box
              sx={{
                width: 2,
                height: 10,
                bgcolor: 'text.primary',
                borderRadius: 0.5,
              }}
            />
          </Box>
        </>
      )}

      {timeline.kind === 'overrun' && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            height: '100%',
            width: '100%',
            bgcolor: RED,
            borderRadius: 1,
          }}
        />
      )}
    </Box>
  );
}

function rightSlotForTimeline(timeline) {
  if (timeline.kind === 'nodates' || timeline.kind === 'invalidSpan') {
    return (
      <Typography sx={{ fontSize: '13px', color: 'text.disabled' }}>
        —
      </Typography>
    );
  }
  if (timeline.kind === 'pre') {
    if (timeline.startedAhead) {
      return (
        <Typography
          sx={{
            fontSize: '12px',
            color: 'text.secondary',
            textAlign: 'right',
            lineHeight: 1.25,
          }}
        >
          Started Ahead of Schedule
        </Typography>
      );
    }
    return (
      <Typography
        sx={{
          fontSize: '12px',
          color: 'text.secondary',
          textAlign: 'right',
          whiteSpace: 'nowrap',
        }}
      >
        Starts in {timeline.days} {timeline.days === 1 ? 'day' : 'days'}
      </Typography>
    );
  }
  if (timeline.kind === 'overrun') {
    return (
      <Typography
        sx={{
          fontSize: '18px',
          fontWeight: 700,
          color: RED,
          textAlign: 'right',
          lineHeight: 1.1,
        }}
      >
        {timeline.overrunPct}%
      </Typography>
    );
  }
  return (
    <Typography
      sx={{
        fontSize: '18px',
        fontWeight: 700,
        textAlign: 'right',
        lineHeight: 1.1,
      }}
    >
      {timeline.schedPct}%
    </Typography>
  );
}

/**
 * Schedule burn bar + right-aligned schedule % (or pre-start / overrun messaging).
 *
 * With `showPlannedActualLayout`, shows **Planned** / **Actual** sub-headers: planned dates
 * always from planned inputs; actual dates from `actualStartDate` / `actualFinishDate`
 * (**Not Set** / blank when missing — field in progress may omit finish).
 *
 * `shopComplete`: planned bar uses performance % (actual finish vs planned window); right % green/red.
 */
const ScheduleRow = ({
  startDate,
  endDate,
  pctSlotWidth = 76,
  shopComplete = false,
  plannedStartDate = null,
  plannedEndDate = null,
  actualStartDate = null,
  actualFinishDate = null,
  showPlannedActualLayout = false,
  reservePillSpace = false,
}) => {
  const model = useMemo(() => {
    const planStartIso = plannedStartDate || startDate || null;
    const planEndIso = plannedEndDate || endDate || null;

    const plannedStartDisplay = planStartIso
      ? fmtEndpoint(planStartIso)
      : 'Not Set';
    const plannedEndDisplay = planEndIso ? fmtEndpoint(planEndIso) : 'Not Set';
    const actualStartDisplay = actualStartDate
      ? fmtEndpoint(actualStartDate)
      : 'Not Set';
    const actualEndDisplay = actualFinishDate
      ? fmtEndpoint(actualFinishDate)
      : '';

    if (shopComplete && actualFinishDate && planStartIso && planEndIso) {
      const planStart = new Date(planStartIso).getTime();
      const planEnd = new Date(planEndIso).getTime();
      const actFinish = new Date(actualFinishDate).getTime();
      const planSpan = planEnd - planStart;

      let finishPct;
      if (
        planSpan > 0 &&
        Number.isFinite(planStart) &&
        Number.isFinite(planEnd)
      ) {
        finishPct = 100 + ((actFinish - planEnd) / planSpan) * 100;
      } else {
        const actD = utcDayMs(actualFinishDate);
        const peD = utcDayMs(planEndIso);
        finishPct = actD != null && peD != null && actD <= peD ? 100 : 101;
      }

      const rounded = Math.round(finishPct);
      const isRed = rounded > 100;
      const barFillPct = isRed ? 100 : clamp(0, 100, rounded);

      return {
        layout: 'shopComplete',
        plannedStartDisplay,
        plannedEndDisplay,
        actualStartDisplay,
        actualEndDisplay: fmtEndpoint(actualFinishDate),
        finishPct: rounded,
        isRed,
        barFillPct,
      };
    }

    const timeline = computeTimeline(startDate, endDate, actualStartDate);

    if (!showPlannedActualLayout) {
      if (timeline.kind === 'nodates' || timeline.kind === 'invalidSpan') {
        return { layout: 'legacyBlank' };
      }
      return { layout: 'legacy', timeline };
    }

    return {
      layout: 'dual',
      timeline,
      plannedStartDisplay,
      plannedEndDisplay,
      actualStartDisplay,
      actualEndDisplay,
    };
  }, [
    shopComplete,
    plannedStartDate,
    plannedEndDate,
    actualStartDate,
    actualFinishDate,
    showPlannedActualLayout,
    startDate,
    endDate,
  ]);

  const rightSlot = () => {
    if (model.layout === 'legacyBlank') {
      return (
        <Typography sx={{ fontSize: '13px', color: 'text.disabled' }}>
          —
        </Typography>
      );
    }
    if (model.layout === 'shopComplete') {
      return (
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 700,
            color: model.isRed ? RED : GREEN,
            textAlign: 'right',
            lineHeight: 1.1,
          }}
        >
          {model.finishPct}%
        </Typography>
      );
    }
    if (model.layout === 'dual' || model.layout === 'legacy') {
      return rightSlotForTimeline(model.timeline);
    }
    return null;
  };
  const isPreStart = model.layout === 'dual' && model.timeline?.kind === 'pre';
  const rightSlotWidth = isPreStart
    ? Math.max(pctSlotWidth, 118)
    : pctSlotWidth;

  const dualActualRow = (endDisplay) => (
    <>
      <Typography sx={{ ...scheduleGroupLabelSx, mt: 1 }}>Actual</Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 0.25,
        }}
      >
        <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>
          {model.actualStartDisplay}
        </Typography>
        <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>
          {endDisplay === '' ? '\u00a0' : endDisplay}
        </Typography>
      </Box>
    </>
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.25 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {model.layout === 'legacyBlank' ? (
          <Typography sx={{ fontSize: '12px', color: 'text.disabled', py: 1 }}>
            No planned dates
          </Typography>
        ) : model.layout === 'shopComplete' ? (
          <>
            <Typography sx={scheduleGroupLabelSx}>Planned</Typography>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}
            >
              <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>
                {model.plannedStartDisplay}
              </Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>
                {model.plannedEndDisplay}
              </Typography>
            </Box>
            <Box
              sx={{
                position: 'relative',
                height: 8,
                borderRadius: 1,
                bgcolor: NEUTRAL,
                overflow: 'hidden',
                mt: reservePillSpace ? PILL_RESERVE_MT : 0.5,
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  height: '100%',
                  width: `${model.barFillPct}%`,
                  bgcolor: model.isRed ? RED : GREEN,
                  borderRadius: 1,
                }}
              />
            </Box>
            {dualActualRow(model.actualEndDisplay)}
          </>
        ) : model.layout === 'dual' ? (
          <>
            <Typography sx={scheduleGroupLabelSx}>Planned</Typography>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}
            >
              <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>
                {model.plannedStartDisplay}
              </Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>
                {model.plannedEndDisplay}
              </Typography>
            </Box>
            {renderPlanBar(model.timeline, reservePillSpace)}
            {dualActualRow(model.actualEndDisplay)}
          </>
        ) : (
          <>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}
            >
              <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>
                {model.timeline.startLabel}
              </Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>
                {model.timeline.endLabel}
              </Typography>
            </Box>
            {renderPlanBar(model.timeline, reservePillSpace)}
          </>
        )}
      </Box>

      <Box
        sx={{
          width: rightSlotWidth,
          flexShrink: 0,
          minHeight: 40,
          display: 'flex',
          alignItems: isPreStart ? 'flex-end' : 'center',
          justifyContent: 'flex-end',
          pt: isPreStart ? 3.1 : 0,
        }}
      >
        {rightSlot()}
      </Box>
    </Box>
  );
};

export default ScheduleRow;
